import { useEffect, useRef } from 'react';
import { ALLOCATION_SERVICE_HOST } from '@app/utils/config';
import { NotAuthenticatedError, getAccessToken } from '@app/utils/keycloak';

/** How long to wait before dialling again after a close or a failed attempt. */
const RECONNECT_DELAY_MS = 3000;

export interface AuthenticatedSocketOptions {
  /** Path on the allocation service, e.g. `/ws/notifications`. */
  path: string;
  /** Every frame the server sends. Throwing here is caught and logged, never left to the socket. */
  onMessage: (event: MessageEvent) => void;
  /**
   * Runs on each successful connect, including reconnects — so this is where anything that repairs
   * what was missed while the socket was down belongs.
   */
  onOpen?: () => void;
}

/**
 * Holds one authenticated websocket open for as long as the component is mounted, redialling
 * whenever it drops.
 *
 * **The handshake is the part worth understanding.** A browser cannot set an Authorization header
 * on a websocket upgrade — the WebSocket constructor takes a url and subprotocols and nothing else —
 * so the token travels as a subprotocol instead, in the `quarkus-http-upgrade#Authorization#Bearer
 * <token>` form Quarkus reads it back out of. The `bearer-token-carrier` protocol listed alongside
 * is what the server selects and echoes; the token-bearing one is never selected, it is only read.
 * The token has to be url-encoded because a subprotocol may not contain arbitrary bytes, and a JWT
 * that happens to carry one would otherwise fail the handshake rather than the authentication.
 *
 * Every retry asks for the token again rather than reusing the one that opened the last connection.
 * A socket that has been down for a while has almost certainly outlived it, and reconnecting with an
 * expired token is a 401 that looks exactly like an outage.
 *
 * Not authenticated at all is the one case that does not retry: nothing will change until the user
 * signs in, and that path re-mounts this.
 */
export const useAuthenticatedSocket = ({ path, onMessage, onOpen }: AuthenticatedSocketOptions) => {
  // Held in refs so a caller passing an inline arrow — which every caller does — does not tear the
  // connection down and dial again on each of its renders.
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  onMessageRef.current = onMessage;
  onOpenRef.current = onOpen;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let isMounted = true;
    let reconnectTimer: ReturnType<typeof setTimeout>;

    const connect = async () => {
      try {
        const token = await getAccessToken();
        if (!isMounted) return;

        const wsUrl = `${ALLOCATION_SERVICE_HOST.replace(/^http/, 'ws')}${path}`;

        const carrier = 'bearer-token-carrier';
        const authProtocol = encodeURIComponent('quarkus-http-upgrade#Authorization#Bearer ' + token);

        ws = new WebSocket(wsUrl, [carrier, authProtocol]);

        ws.onopen = () => {
          console.info(
            `[WebSocket] OPEN ${wsUrl} — server selected subprotocol: ${JSON.stringify(ws?.protocol)}`
          );
          onOpenRef.current?.();
        };

        ws.onmessage = (event) => {
          try {
            onMessageRef.current(event);
          } catch (err) {
            console.error(`[WebSocket] ${path} failed to apply a message:`, err, event.data);
          }
        };

        ws.onerror = (error) => {
          console.error(`[WebSocket] ${path} connection error:`, error);
        };

        ws.onclose = (event) => {
          console.warn(
            `[WebSocket] CLOSED ${path} code=${event.code} reason=${event.reason || '(none)'} clean=${event.wasClean}`
          );
          if (isMounted) {
            reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS); // fresh token each retry
          }
        };

      } catch (error) {
        if (error instanceof NotAuthenticatedError) {
          console.warn(`[WebSocket] No session; not connecting to ${path}.`);
          return; // don't retry — nothing will change until they log in
        }
        console.error(`[WebSocket] Failed to authenticate for ${path}:`, error);
        if (isMounted) reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    connect();

    return () => {
      isMounted = false;
      clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
  }, [path]);
};
